import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MgmtLayout from "../MgmtLayout";
import { api } from "../../api";
import { useAuth } from "../../AuthContext";
import { toast } from "sonner";
import { Plus, Loader2, LogOut } from "lucide-react";

/**
 * MgmtQuickUsage — one-tap +100g logger.
 * Crew taps a grade tile → backend appends a 100g bottle to today's log →
 * stock drains by 100g instantly. Undo button in the toast refunds.
 */
export default function MgmtQuickUsage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [grades, setGrades] = useState({ matcha: [], hojicha: [], coffee: [] });
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({}); // {ingredient_id: true}

  const load = async () => {
    try {
      const { data } = await api.get("/mgmt/powder-grades");
      setGrades({
        matcha:  data.matcha  || [],
        hojicha: data.hojicha || [],
        coffee:  data.coffee  || [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const tap = async (g) => {
    if (pending[g.id]) return;
    setPending((p) => ({ ...p, [g.id]: true }));
    try {
      const { data } = await api.post("/mgmt/quick-usage", { variety_id: g.id });
      toast.success(`+100g ${g.name}`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.delete(`/mgmt/quick-usage/${data.bottle_key}`);
              toast("Refunded 100g to " + g.name);
              load();
            } catch {
              toast.error("Undo failed");
            }
          },
        },
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log");
    } finally {
      setPending((p) => { const n = { ...p }; delete n[g.id]; return n; });
    }
  };

  const Section = ({ label, kind, list, accent, bg }) => (
    <section className="mb-10" data-testid={`qu-section-${kind}`}>
      <h2 className={`overline text-[11px] mb-3 ${accent}`}>{label}</h2>
      {list.length === 0 ? (
        <p className="text-sm text-ink-soft italic">No {kind} grades defined yet. Add them in Products & Inventory.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {list.map((g) => {
            const low = Number(g.stock_qty || 0) <= 0.5;
            const stockLabel = g.stock_qty != null
              ? `${Number(g.stock_qty).toFixed(1)} ${g.unit || ""}`.trim()
              : "—";
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => tap(g)}
                disabled={!!pending[g.id]}
                data-testid={`qu-tile-${g.id}`}
                className={`group relative text-left rounded-sm border transition-all duration-200 active:scale-[0.97]
                  ${low ? "border-red-400/60 bg-red-50/40" : `border-ink/10 ${bg}`}
                  hover:border-ink/40 hover:shadow-md
                  disabled:opacity-60 p-4 min-h-[112px]`}
              >
                <div className="flex items-start justify-between">
                  <p className="overline text-ink-soft text-[9px]">{kind}</p>
                  {pending[g.id] ? <Loader2 size={14} className="animate-spin text-ink-soft" /> : <Plus size={14} className={accent + " opacity-60 group-hover:opacity-100"} />}
                </div>
                <p className="font-display text-lg md:text-xl text-ink mt-1 leading-tight">{g.name}</p>
                <p className={`text-[11px] mt-2 tabular-nums ${low ? "text-red-700" : "text-ink-soft"}`}>
                  Stock {stockLabel}
                </p>
                <p className="text-[10px] uppercase tracking-[0.14em] text-ink mt-2">+100g · tap</p>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );

  const Body = (
    <div className="max-w-5xl">
      <p className="text-sm text-ink-soft mb-6">
        Tap a grade to log a fresh 100g top-up for today. Stock drains instantly and the entry
        shows up in Daily Powder & the Monthly chart. Hit <span className="font-medium text-ink">Undo</span> in the toast if you tapped by mistake.
      </p>

      {loading ? (
        <p className="text-ink-soft">Loading grades…</p>
      ) : (
        <>
          <Section label="Matcha grades" kind="matcha" list={grades.matcha}
            accent="text-matcha-dark" bg="bg-sage/15" />
          <Section label="Hojicha" kind="hojicha" list={grades.hojicha}
            accent="text-amber-800" bg="bg-amber-50/40" />
          <Section label="Coffee" kind="coffee" list={grades.coffee}
            accent="text-stone-700" bg="bg-stone-50" />
        </>
      )}
    </div>
  );

  if (isAdmin) {
    return (
      <MgmtLayout subtitle="Quick Usage" title="One-Tap +100g Logger">
        {Body}
      </MgmtLayout>
    );
  }

  // Crew (non-admin) — simpler shell, no admin sidebar.
  const onLogout = () => { logout(); navigate("/staff/login"); };
  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="border-b border-ink/10 bg-bone/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="px-4 md:px-10 py-3 md:py-5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="overline text-matcha-dark text-[0.6rem] md:text-xs">Quick Usage</p>
            <h1 className="font-display text-ink truncate" style={{ fontSize: "clamp(1.05rem, 2.4vw, 2rem)", letterSpacing: "0.04em" }}>
              One-Tap +100g Logger
            </h1>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Link to="/staff/dashboard" data-testid="qu-back-dashboard"
              className="text-[10px] md:text-xs uppercase tracking-[0.14em] text-ink-soft hover:text-ink">
              ← Dashboard
            </Link>
            <button
              data-testid="qu-logout-btn"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full px-3 md:px-4 py-2 border border-ink/30 hover:bg-ink hover:text-bone transition-all duration-300 text-[10px] md:text-xs uppercase tracking-[0.14em]"
            >
              <LogOut size={14} />
              <span className="hidden xs:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>
      <main className="px-4 md:px-10 py-6 md:py-12">{Body}</main>
    </div>
  );
}
